import { useRouter } from 'next/router';
import { memo, useEffect, useRef } from 'react';

import { type LeadFormTrigger, type LeadFormUILocation, useTrackLeadFormOpened } from '@air/analytics';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';

import { DefaultPixelSdkScript } from '~/components/default-pixel/DefaultPixelSdkScript';
import { DemoForm } from '~/components/Modals/DemoForm';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

export type DemoFormModalProps = {
  trigger?: LeadFormTrigger;
  uiLocation?: LeadFormUILocation;
};

export const DemoFormModal = memo(({ onClose, trigger, uiLocation }: AirModalProps<DemoFormModalProps>) => {
  const { data: subscription } = useSubscriptionContext();
  const { trackLeadFormOpened } = useTrackLeadFormOpened();
  const router = useRouter();

  const triggerValue = trigger ?? `page:${router.pathname}`;
  const hasTrackedOpenRef = useRef<boolean>();

  useEffect(() => {
    if (hasTrackedOpenRef.current || !subscription?.plan) {
      return;
    }

    hasTrackedOpenRef.current = true;
    trackLeadFormOpened({ plan: subscription.plan, trigger: triggerValue });
  }, [subscription?.plan, trackLeadFormOpened, triggerValue]);

  return (
    <>
      <DefaultPixelSdkScript />
      <Modal
        className="flex flex-col gap-4"
        dangerouslyBypassFocusLock
        dangerouslyBypassScrollLock
        isOpen
        onDismiss={onClose}
        data-testid="DEMO_FORM_MODAL"
      >
        <header className="flex flex-col gap-2">
          <div className="flex justify-between gap-2">
            <ModalTitle>Let&apos;s talk</ModalTitle>
            <ModalCloseButton onClick={onClose} />
          </div>
          <ModalDescription>
            Schedule a free demo today. One of our experts will show you how Air works.
          </ModalDescription>
        </header>

        <DemoForm onClose={onClose} uiLocation={uiLocation} />
      </Modal>
    </>
  );
});

DemoFormModal.displayName = 'DemoFormModal';
